import React from 'react';
import { motion } from 'framer-motion';
import { COMPANY_INFO, LEGAL_LAST_UPDATED } from '../config/company';
import { ObfuscatedEmail } from '../components/ObfuscatedEmail';

export const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 pt-24 pb-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-8"
        >
          {/* Header */}
          <div className="text-center mb-12">
            <p className="ui-eyebrow-brand mb-4">Legal</p>
            <h1 className="ui-page-title mb-4">Privacy & Cookie Policy</h1>
            <p className="text-sm text-slate-500">Last updated: {LEGAL_LAST_UPDATED}</p>
          </div>

          {/* Single Content Block */}
          <div className="prose prose-slate max-w-none text-slate-600">
            <p className="text-lg leading-relaxed mb-8">
              {COMPANY_INFO.legalName} ({COMPANY_INFO.brandName}) is committed to protecting your privacy. This page
              explains what personal data we may process, how we use it, and how cookies and similar technologies work
              on this website.
            </p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">Who We Are</h2>
            <p>
              {COMPANY_INFO.legalName}<br />
              {COMPANY_INFO.address.line1}, {COMPANY_INFO.address.line2}<br />
              Tax ID: {COMPANY_INFO.taxId}<br />
              Registry: {COMPANY_INFO.registry}
            </p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">What We May Collect</h2>
            <ul className="space-y-2 list-disc pl-5">
              <li>Contact details such as your name, email address, phone number, and message when you enquire.</li>
              <li>Booking or travel details such as dates, guest numbers, and stay preferences.</li>
              <li>Technical and usage data such as browser type, IP address, pages viewed, and site interactions.</li>
              <li>Saved preferences such as favorites, searches, and cookie choices stored in your browser.</li>
            </ul>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">How We Use Personal Data</h2>
            <ul className="space-y-2 list-disc pl-5">
              <li>To respond to enquiries and help with bookings or owner requests.</li>
              <li>To operate the website and remember preferences you ask us to keep.</li>
              <li>To measure performance and improve the site, where analytics has been accepted.</li>
              <li>To comply with legal obligations and protect our business and users.</li>
            </ul>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">Who We May Share Data With</h2>
            <p>
              We may share limited data with service providers that help us run the site or deliver requested services,
              including hosting and security providers, analytics providers, communication tools such as WhatsApp, and
              booking-related platforms where relevant. If analytics is enabled, we may use Google Analytics and may
              also enable Cloudflare Web Analytics.
            </p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">Cookies and Similar Technologies</h2>
            <ul className="space-y-2 list-disc pl-5">
              <li><strong>Essential cookies and storage:</strong> used for site function, favorites, searches, and your cookie preference.</li>
              <li><strong>Analytics cookies:</strong> used by Google Analytics only if you accept analytics cookies.</li>
              <li><strong>Cloudflare Web Analytics:</strong> may be enabled for aggregate reporting; it is listed for transparency.</li>
            </ul>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">Storage We Currently Use</h2>
            <ul className="space-y-2 list-disc pl-5">
              <li><strong>sunmarbella_cookie_consent_v1:</strong> stores your cookie choice.</li>
              <li><strong>sunmarbella_favorites:</strong> remembers properties you save.</li>
              <li><strong>searchParams:</strong> keeps your search dates and guest preferences.</li>
              <li><strong>_ga</strong> and <strong>_ga_&lt;container-id&gt;:</strong> used by Google Analytics if analytics is accepted.</li>
              <li><strong>sunmarbella_newsletter_signed_up</strong> and <strong>sunmarbella_newsletter_demo_signups:</strong> support the current newsletter UX state.</li>
            </ul>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">Managing Cookies</h2>
            <p>
              You can manage cookie preferences through the cookie settings control on the site. You can also remove or
              block cookies in your browser settings, although some site functions may then work less smoothly.
            </p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">Your Rights</h2>
            <p>
              You can request access to, correction of, or deletion of your personal data, and you can object to or
              restrict certain processing where the law allows. Contact us at <ObfuscatedEmail className="text-brand-ocean hover:underline" /> or lodge a complaint with the Spanish Data Protection Agency (AEPD).
            </p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">Contact</h2>
            <p>
              {COMPANY_INFO.legalName}<br />
              {COMPANY_INFO.address.line1}, {COMPANY_INFO.address.line2}<br />
              {COMPANY_INFO.phone}<br />
              <ObfuscatedEmail className="text-brand-ocean hover:underline" />
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
